import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  EmptyState,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@wmart/ui";
import { api } from "../../lib/api-client.js";
import { formatDate } from "../../lib/format.js";
import { useToast } from "../../lib/toast.js";
import type {
  PortalPoSummaryDTO,
  SupplierOrderStatus,
} from "@wmart/shared-types";
import { PO_STATUS_LABEL, PO_STATUS_TONE } from "./po-status.js";

type TabKey = "new" | "in_progress" | "ready" | "all";

const TABS: { key: TabKey; label: string }[] = [
  { key: "new", label: "New" },
  { key: "in_progress", label: "In progress" },
  { key: "ready", label: "Ready" },
  { key: "all", label: "All" },
];

/** Client-side tab predicate over the single fetched PO list. */
function matchesTab(status: SupplierOrderStatus, tab: TabKey): boolean {
  switch (tab) {
    case "new":
      return status === "new";
    case "in_progress":
      return status === "acknowledged" || status === "preparing";
    case "ready":
      return status === "ready";
    case "all":
      return true;
  }
}

/**
 * Supplier portal — purchase-order queue. Fetches every PO scoped to the
 * session supplier once (`GET /v1/supplier/orders`) and filters client-side
 * across the New / In progress / Ready / All tabs. Rows link through to the
 * PO detail page.
 */
export function SupplierPoList() {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [pos, setPos] = useState<PortalPoSummaryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState<TabKey>("new");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await api.get<{ orders: PortalPoSummaryDTO[] }>(
          "/v1/supplier/orders",
        );
        if (cancelled) return;
        setPos(res.orders);
        setLoadError(false);
      } catch {
        if (cancelled) return;
        setLoadError(true);
        notify("error", "Failed to load purchase orders");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [notify]);

  const filtered = useMemo(
    () => pos.filter((po) => matchesTab(po.status, tab)),
    [pos, tab],
  );

  return (
    <div className="flex flex-col gap-6">
      <h1 className="font-serif text-display-sm text-ink-900">
        Purchase orders
      </h1>

      {/* Segment control, NOT an ARIA tablist — plain toggle buttons with
          aria-pressed (the DensityToggle idiom). A real tablist demands
          aria-controls/tabpanel/arrow-key wiring that nothing else in the
          admin app implements; pressed buttons are the simpler, honest
          semantics for a client-side filter. */}
      <div
        role="group"
        aria-label="Purchase order status filter"
        className="flex flex-wrap gap-1 border-b border-ink-200"
      >
        {TABS.map((t) => {
          const count =
            t.key === "all"
              ? pos.length
              : pos.filter((po) => matchesTab(po.status, t.key)).length;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              aria-pressed={active}
              onClick={() => setTab(t.key)}
              className={
                active
                  ? "border-b-2 border-accent px-3 py-2 text-body-sm font-medium text-ink-900"
                  : "border-b-2 border-transparent px-3 py-2 text-body-sm text-ink-500 hover:text-ink-700"
              }
            >
              {t.label}
              <span className="ml-1.5 text-caption text-ink-400">{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-body text-ink-500">Loading…</p>
      ) : loadError ? (
        <p className="text-body-sm text-danger">
          Purchase orders could not be loaded.
        </p>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="package"
          title="No purchase orders"
          body={
            tab === "all"
              ? "When WMart places an order with you it will appear here."
              : "Nothing in this list right now."
          }
        />
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <THead>
              <TR>
                <TH>PO number</TH>
                <TH>Order ref</TH>
                <TH>Created</TH>
                <TH className="text-right">Items</TH>
                <TH>Needed by</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {filtered.map((po) => (
                <TR
                  key={po.id}
                  onClick={() => navigate(`/supplier/orders/${po.id}`)}
                  className="cursor-pointer hover:bg-paper-warm"
                >
                  <TD className="font-mono text-body-sm">{po.po_number}</TD>
                  <TD className="font-mono text-body-sm text-ink-500">
                    {po.order_ref}
                  </TD>
                  <TD className="text-body-sm text-ink-500">
                    {formatDate(po.created_at)}
                  </TD>
                  <TD className="text-right">{po.item_count}</TD>
                  <TD className="text-body-sm text-ink-500">
                    {po.needed_by ? formatDate(po.needed_by) : "—"}
                  </TD>
                  <TD>
                    <StatusPill tone={PO_STATUS_TONE[po.status]}>
                      {PO_STATUS_LABEL[po.status]}
                    </StatusPill>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
